import { WritableSignal } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

import { LoggerService } from 'app/services/logger.service';
import { StreamingCallbacks, StreamingToolCall } from 'app/services/spotlight-agent-chat.service';
import { ChartData } from './ai-chart/ai-chart.component';
import { RICH_TOOL_NAMES, ToolContent } from './tool-content.model';

export type OrchestratorChartOutput = ChartData;

export interface OrchestratorMessage {
  role: 'user' | 'assistant' | 'tool';
  content?: SafeHtml;
  text: string;
  mode?: string;
  chartData?: OrchestratorChartOutput;
  chartStatus?: 'loading' | 'error';
  chartToolCallId?: string;
  chartToolName?: string;
  toolContent?: ToolContent;
  toolContentStatus?: 'loading' | 'error';
  toolContentToolCallId?: string;
  toolContentToolName?: string;
}

/**
 * Delegate interface for operations that require the host component.
 * Keeps the orchestrator free of Angular/DOM dependencies.
 */
export interface StreamingChatDelegate {
  createEmptyAssistantMessage(): OrchestratorMessage;
  updateStreamingMessage(index: number, text: string): void;
  insertToolPlaceholderAfter(
    streamingMessageIndex: number | null,
    toolCallId: string | undefined,
    toolName: string
  ): number;
  resolveToolPlaceholder(
    toolCallId: string | undefined,
    toolName: string,
    output: unknown
  ): boolean;
  addToolContentMessage(toolName: string, output: unknown): void;
  normalizeToolOutput(toolName: string, output: unknown): unknown;
  stripMarkdownImages(text: string): string;
  setWaitingForResponse(value: boolean): void;
  setTyping(value: boolean): void;
  handleStreamingFallback(
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    dataSource: unknown
  ): void;
  fetchToolOutputs(sessionId: string): void;
  hasLoadingToolPlaceholders(): boolean;
  syncHistory(): void;
  addSessionExpiredMessage(): void;
  addErrorMessage(errorText: string): void;
}

/**
 * Encapsulates the streaming chat state machine.
 * Tracks text accumulation, chart placeholder lifecycle, and deduplication
 * so the host component only needs to implement the delegate interface.
 */
export class StreamingChatOrchestrator {
  private streamingText = '';
  private streamingMessageIndex: number | null = null;
  private hasChunk = false;
  private pendingToolCount = 0;
  private resolvedToolCount = 0;
  private readonly pendingToolCallIds = new Set<string>();
  private readonly resolvedToolCallIds = new Set<string>();
  private hasUnnamedPendingTool = false;
  private readonly queuedToolCalls: Array<{
    toolCallId: string | undefined;
    toolName: string;
  }> = [];
  private readonly queuedOutputsByToolCallId = new Map<string, unknown>();
  private readonly queuedUnnamedOutputs: unknown[] = [];

  constructor(
    private readonly messages: WritableSignal<OrchestratorMessage[]>,
    private readonly delegate: StreamingChatDelegate,
    private readonly sessionId: string,
    private readonly chatMessages: Array<{ role: 'user' | 'assistant'; content: string }>,
    private readonly dataSource: unknown,
    private readonly logger: LoggerService
  ) {}

  private hasToolInsertionBoundary(text: string): boolean {
    const trimmedText = text.trimEnd();
    return /[.!?]["')\]]?$/.test(trimmedText) || /\n\s*\n/.test(text);
  }

  private canInsertToolPlaceholder(): boolean {
    const strippedText = this.delegate.stripMarkdownImages(this.streamingText);
    return this.hasToolInsertionBoundary(strippedText);
  }

  private syncWaitingIndicator(): void {
    const hasHiddenPendingTool =
      this.hasChunk &&
      this.pendingToolCount > this.resolvedToolCount &&
      !this.delegate.hasLoadingToolPlaceholders();
    this.delegate.setWaitingForResponse(!this.hasChunk || hasHiddenPendingTool);
  }

  get hasUnresolvedTools(): boolean {
    return (
      this.pendingToolCount > this.resolvedToolCount || this.delegate.hasLoadingToolPlaceholders()
    );
  }

  createCallbacks(): StreamingCallbacks {
    return {
      onTextChunk: (text: string) => this.handleTextChunk(text),
      onToolCall: (tool: StreamingToolCall) => this.handleToolCall(tool),
      onComplete: () => this.handleComplete(),
      onError: (error: Error) => this.handleError(error),
    };
  }

  private handleTextChunk(text: string): void {
    this.streamingText += text;
    this.hasChunk = true;
    if (this.streamingMessageIndex === null) {
      this.streamingMessageIndex = this.messages().length;
      this.messages.update((msgs) => [...msgs, this.delegate.createEmptyAssistantMessage()]);
    }
    this.delegate.updateStreamingMessage(
      this.streamingMessageIndex,
      this.delegate.stripMarkdownImages(this.streamingText)
    );
    this.flushQueuedToolCallsIfReady();
    this.syncWaitingIndicator();
  }

  private flushQueuedToolCallsIfReady(force: boolean = false): void {
    if (this.queuedToolCalls.length === 0) {
      return;
    }
    if (!force && !this.canInsertToolPlaceholder()) {
      return;
    }
    while (this.queuedToolCalls.length > 0) {
      const nextToolCall = this.queuedToolCalls.shift();
      if (!nextToolCall) {
        break;
      }
      this.streamingMessageIndex = this.delegate.insertToolPlaceholderAfter(
        this.streamingMessageIndex,
        nextToolCall.toolCallId,
        nextToolCall.toolName
      );
      if (nextToolCall.toolCallId) {
        const queuedOutput = this.queuedOutputsByToolCallId.get(nextToolCall.toolCallId);
        if (queuedOutput) {
          this.delegate.resolveToolPlaceholder(
            nextToolCall.toolCallId,
            nextToolCall.toolName,
            queuedOutput
          );
          this.queuedOutputsByToolCallId.delete(nextToolCall.toolCallId);
        }
      } else if (this.queuedUnnamedOutputs.length > 0) {
        const nextOutput = this.queuedUnnamedOutputs.shift();
        if (nextOutput) {
          this.delegate.resolveToolPlaceholder(undefined, nextToolCall.toolName, nextOutput);
        }
      }
    }
    this.streamingText = '';
  }

  private handleToolCall(tool: StreamingToolCall): void {
    if (!RICH_TOOL_NAMES.has(tool.toolName)) {
      return;
    }
    if (tool.state === 'pending') {
      this.handlePendingTool(tool);
      return;
    }
    if (tool.state === 'output-available') {
      this.handleToolOutput(tool);
    }
  }

  private handlePendingTool(tool: StreamingToolCall): void {
    const toolCallId = tool.toolCallId;
    if (toolCallId) {
      if (this.pendingToolCallIds.has(toolCallId)) {
        return;
      }
      this.pendingToolCallIds.add(toolCallId);
    } else {
      if (this.hasUnnamedPendingTool) {
        return;
      }
      this.hasUnnamedPendingTool = true;
    }
    this.pendingToolCount += 1;
    if (!this.canInsertToolPlaceholder()) {
      this.queuedToolCalls.push({ toolCallId, toolName: tool.toolName });
      this.syncWaitingIndicator();
      return;
    }
    this.streamingMessageIndex = this.delegate.insertToolPlaceholderAfter(
      this.streamingMessageIndex,
      toolCallId,
      tool.toolName
    );
    this.streamingText = '';
    this.syncWaitingIndicator();
  }

  private handleToolOutput(tool: StreamingToolCall): void {
    const toolCallId = tool.toolCallId;
    if (toolCallId) {
      if (this.resolvedToolCallIds.has(toolCallId)) {
        return;
      }
      this.resolvedToolCallIds.add(toolCallId);
    }
    const normalized = this.delegate.normalizeToolOutput(tool.toolName, tool.output);
    if (!this.canInsertToolPlaceholder() && normalized) {
      this.queueToolOutputForLater(toolCallId, tool.toolName, normalized);
      this.resolvedToolCount += 1;
      return;
    }
    if (normalized) {
      const resolved = this.delegate.resolveToolPlaceholder(
        tool.toolCallId,
        tool.toolName,
        normalized
      );
      if (!resolved) {
        this.delegate.addToolContentMessage(tool.toolName, normalized);
      }
      this.resolvedToolCount += 1;
    } else {
      this.delegate.resolveToolPlaceholder(tool.toolCallId, tool.toolName, null);
      this.logger.warn(`[StreamingChatOrchestrator] ${tool.toolName} output missing or invalid`, {
        output: tool.output,
      });
    }
    this.syncWaitingIndicator();
  }

  private queueToolOutputForLater(
    toolCallId: string | undefined,
    toolName: string,
    output: unknown
  ): void {
    if (toolCallId) {
      this.queuedOutputsByToolCallId.set(toolCallId, output);
      if (!this.pendingToolCallIds.has(toolCallId)) {
        this.pendingToolCallIds.add(toolCallId);
        this.pendingToolCount += 1;
        this.queuedToolCalls.push({ toolCallId, toolName });
      }
    } else {
      this.queuedUnnamedOutputs.push(output);
      if (!this.hasUnnamedPendingTool) {
        this.hasUnnamedPendingTool = true;
        this.pendingToolCount += 1;
        this.queuedToolCalls.push({ toolCallId: undefined, toolName });
      }
    }
  }

  private handleComplete(): void {
    this.delegate.setWaitingForResponse(false);
    if (!this.hasChunk) {
      this.delegate.handleStreamingFallback(this.sessionId, this.chatMessages, this.dataSource);
    } else {
      this.delegate.updateStreamingMessage(
        this.streamingMessageIndex,
        this.delegate.stripMarkdownImages(this.streamingText)
      );
      this.flushQueuedToolCallsIfReady(true);
    }
    if (this.hasUnresolvedTools) {
      this.delegate.fetchToolOutputs(this.sessionId);
    }
    this.delegate.setTyping(false);
    this.delegate.syncHistory();
  }

  private handleError(error: Error): void {
    if (error.name === 'AbortError') {
      this.delegate.setWaitingForResponse(false);
      this.delegate.setTyping(false);
      return;
    }
    this.logger.error('[StreamingChatOrchestrator] streaming error:', { error });
    this.delegate.setWaitingForResponse(false);
    if (error.message === 'SESSION_EXPIRED') {
      this.delegate.addSessionExpiredMessage();
      this.delegate.setTyping(false);
      return;
    }
    if (!this.hasChunk) {
      this.delegate.handleStreamingFallback(this.sessionId, this.chatMessages, this.dataSource);
    } else {
      this.delegate.addErrorMessage(error.message || 'An error occurred');
    }
    this.delegate.setTyping(false);
  }
}
